import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { AppShell } from '../../components/AppShell';
import { useMe, useModuleEnabled } from '../../lib/auth';
import {
  agingDateCopy,
  canAccessFeeCenter,
  defaultFeeCenterPeriod,
  expectedFeeUnavailableCopy,
  feeCenterCoverageCopy,
  feeCenterProvenanceLabel,
  formatFeeCenterCurrency,
  formatFeeCenterDate,
  formatFeeCenterDelta,
  formatFeeCenterPercent,
  formatFeeCenterProductUnitCost,
  formatFeeCenterSurchargeCountdown,
  formatFeeCenterUnitsPerDay,
  formatFeeCenterVelocityUnavailable,
  feeCenterActivityDirection,
  feeCenterActivityLinkage,
  feeCenterActualSummary,
  feeCenterFulfillmentEvidenceCopy,
  feeCenterKernelMatch,
  feeCenterProductIdentifiers,
  feeCenterProductTitle,
  rankFulfillmentByDelta,
  rankAccountActivity,
  summarizeAccountActivity,
  useFeeCenter,
  type FeeCenterPeriod,
} from '../../lib/fee-center';
import { Badge, Card, Input } from '../../design';
import { Skeleton, SkeletonLine } from '../../components/Skeleton';
import type {
  FeeCenterAccountActivityRow,
  FeeCenterActualRow,
  FeeCenterAgedRow,
  FeeCenterFeeChangeRow,
  FeeCenterFulfillmentAuditRow,
  FeeCenterProvenance,
  FeeCenterStorageRow,
  FeeCenterTotalsRow,
} from '@sourcito/schemas';

function monthBounds(month: string): string {
  const [year, monthNumber] = month.split('-').map(Number);
  if (!year || !monthNumber) return month;
  return `${year}-${String(monthNumber).padStart(2, '0')}-01`;
}

function monthEnd(month: string): string {
  const [year, monthNumber] = month.split('-').map(Number);
  if (!year || !monthNumber) return month;
  return new Date(Date.UTC(year, monthNumber, 0)).toISOString().slice(0, 10);
}

function monthFromDate(date: string): string {
  return date.slice(0, 7);
}

function unavailable(label: string): string {
  return `Withheld — ${label}`;
}

/** R2 item 3 (D4, docs/plans/2026-09-03-graded-kernel.md) — "matches
 *  Finance" / "differs from Finance by $X" note under the page description,
 *  from the kernel cross-check (D3) `totals` carry. Renders nothing (not
 *  even a skeleton) when the API build predates `totals` or the period has
 *  no settlement currency — an absent note is honest, not a false "matches". */
function KernelMatchNote({ totals }: { totals: readonly FeeCenterTotalsRow[] | undefined }) {
  const match = feeCenterKernelMatch(totals);
  if (match.status === 'unknown') return null;
  if (match.status === 'matches') {
    return (
      <p className="mt-1.5">
        <Badge variant="positive">Matches Finance</Badge>
      </p>
    );
  }
  return (
    <p className="mt-1.5">
      <Badge variant="warning">Differs from Finance by {match.label}</Badge>
    </p>
  );
}

export function FeeCenterPage() {
  const { data: me } = useMe();
  const enabled = useModuleEnabled('amazon');
  const canOpen = canAccessFeeCenter(me?.user.role, enabled);
  const [period, setPeriod] = useState<FeeCenterPeriod>(() => defaultFeeCenterPeriod());
  const query = useFeeCenter(period, canOpen);

  if (me?.user.role === 'warehouse') {
    return (
      <AppShell>
        <main className="max-w-3xl mx-auto px-8 py-16">
          <Card>
            <p className="text-sm text-fg-secondary">
              Warehouse accounts can&rsquo;t access Finance.{' '}
              <Link to="/app/receiving" className="text-accent-fg hover:underline">
                Go to Receiving
              </Link>
            </p>
          </Card>
        </main>
      </AppShell>
    );
  }

  if (!enabled) {
    return (
      <AppShell>
        <main className="max-w-3xl mx-auto px-8 py-16">
          <Card>
            <p className="text-sm text-fg-secondary">
              The Amazon module isn&rsquo;t enabled for this workspace.{' '}
              <Link to="/app/finance" className="text-accent-fg hover:underline">
                Back to Finance
              </Link>
            </p>
          </Card>
        </main>
      </AppShell>
    );
  }

  if (!canOpen) {
    return (
      <AppShell>
        <main className="max-w-3xl mx-auto px-8 py-16">
          <Card>
            <p className="text-sm text-fg-secondary">
              Fee Center is available to owner and operator accounts.{' '}
              <Link to="/app/finance" className="text-accent-fg hover:underline">
                Back to Finance
              </Link>
            </p>
          </Card>
        </main>
      </AppShell>
    );
  }

  const data = query.data;

  return (
    <AppShell>
      <header className="border-b border-border-subtle">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 py-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-fg-tertiary">FINANCE</p>
              <h1 className="font-display text-3xl font-medium leading-tight mt-2">Fee Center</h1>
              <p className="text-sm text-fg-secondary mt-2">
                Understand what Amazon charged or credited, what ties to products, and which checks
                remain separate evidence.
              </p>
              {data && <KernelMatchNote totals={data.totals} />}
            </div>
            <MonthRangeControls period={period} onChange={setPeriod} />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-8 py-8 space-y-8">
        {query.isError && (
          <Card>
            <p className="text-sm text-negative">
              Could not load Fee Center:{' '}
              {query.error instanceof Error ? query.error.message : 'request failed'}
            </p>
            <p className="text-xs text-fg-tertiary mt-1">
              Check the selected complete calendar months and try again.
            </p>
          </Card>
        )}
        {query.isLoading && (
          <>
            <p className="sr-only" role="status" aria-live="polite">
              Loading Fee Center…
            </p>
            <FeeCenterSkeleton />
          </>
        )}
        {data && (
          <>
            {!data.coverage.complete && <CoverageBanners coverage={data.coverage} />}
            <AccountActivitySection rows={data.accountActivity} />
            <ActualSection rows={data.actual} fulfillment={data.fulfillment} />
            <FulfillmentSection rows={data.fulfillment} />
            <StorageSection rows={data.storage} />
            <AgedSection rows={data.aged} />
            <FeeChangesSection rows={data.feeChanges} />
          </>
        )}
      </main>
    </AppShell>
  );
}

// Coarse shimmer for the six report sections below (account activity, fee
// checks, fulfillment audit, storage, aged inventory, fee changes) — every
// section shares the same "grid of cards with a title/subtitle, a 2x2
// metric grid, and a provenance line" shape, so one repeated block stands
// in for all of them rather than six bespoke skeletons.
function FeeCenterSkeleton() {
  return (
    <div className="space-y-8" aria-hidden="true">
      {[0, 1, 2].map((section) => (
        <div key={section}>
          <SkeletonLine className="h-5 w-48 rounded-xs" />
          <SkeletonLine className="h-3.5 w-96 rounded-xs mt-2" />
          <div className="grid gap-3 sm:grid-cols-2 mt-3">
            {[0, 1].map((card) => (
              <Card key={card}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <SkeletonLine className="h-4 w-32" />
                    <SkeletonLine className="h-3 w-24 mt-2" />
                  </div>
                  <Skeleton className="h-5 w-16 rounded-full shrink-0" />
                </div>
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <SkeletonLine className="h-3 w-16" />
                  <SkeletonLine className="h-3 w-16" />
                  <SkeletonLine className="h-3 w-16" />
                  <SkeletonLine className="h-3 w-16" />
                </div>
                <SkeletonLine className="h-2.5 w-40 mt-4" />
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function MonthRangeControls({
  period,
  onChange,
}: {
  period: FeeCenterPeriod;
  onChange: (period: FeeCenterPeriod) => void;
}) {
  const from = monthFromDate(period.from);
  const to = monthFromDate(period.to);
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <label className="flex items-center gap-2 text-xs text-fg-tertiary">
        From
        <Input
          type="month"
          inputSize="sm"
          value={from}
          onChange={(event) => onChange({ from: monthBounds(event.target.value), to: period.to })}
          aria-label="Fee Center start month"
        />
      </label>
      <label className="flex items-center gap-2 text-xs text-fg-tertiary">
        To
        <Input
          type="month"
          inputSize="sm"
          value={to}
          onChange={(event) => onChange({ from: period.from, to: monthEnd(event.target.value) })}
          aria-label="Fee Center end month"
        />
      </label>
    </div>
  );
}

function CoverageBanners({
  coverage,
}: {
  coverage: {
    storageFees: { status: 'unknown' | 'backfilling' | 'caught_up' | 'pending' };
    inventoryPlanning: { status: 'unknown' | 'backfilling' | 'caught_up' | 'pending' };
  };
}) {
  const banners = [
    feeCenterCoverageCopy(coverage.storageFees.status, 'Storage fees'),
    feeCenterCoverageCopy(coverage.inventoryPlanning.status, 'Inventory planning'),
  ].filter((banner): banner is { title: string; detail: string } => banner !== null);
  if (banners.length === 0) return null;
  return (
    <div className="space-y-2" aria-label="Fee Center source coverage">
      {banners.map((banner) => (
        <div
          key={banner.title}
          className="border border-warning/30 bg-warning/[0.08] rounded-xl px-4 py-3"
        >
          <p className="text-sm font-medium text-fg-primary">{banner.title}</p>
          <p className="text-xs text-fg-secondary mt-1">{banner.detail}</p>
        </div>
      ))}
    </div>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section aria-labelledby={`${title.toLowerCase().replaceAll(' ', '-')}-heading`}>
      <div className="mb-3">
        <h2
          id={`${title.toLowerCase().replaceAll(' ', '-')}-heading`}
          className="text-lg font-medium"
        >
          {title}
        </h2>
        <p className="text-xs text-fg-tertiary mt-1">{description}</p>
      </div>
      {children}
    </section>
  );
}

function Provenance({ value }: { value: FeeCenterProvenance }) {
  return (
    <p className="text-[11px] text-fg-tertiary mt-3">
      Source · period · freshness · coverage: {feeCenterProvenanceLabel(value)}
    </p>
  );
}

function AccountActivitySection({ rows }: { rows: FeeCenterAccountActivityRow[] }) {
  const ordered = rankAccountActivity(rows);
  const totals = summarizeAccountActivity(rows);
  return (
    <Section
      title="Where the money went"
      description="Every non-revenue settlement movement is grouped by month and raw Amazon label. Positive amounts are charges; negative amounts are credits."
    >
      {ordered.length === 0 ? (
        <Card>
          <p className="text-sm text-fg-secondary">No account activity in this period.</p>
        </Card>
      ) : (
        <>
          <div
            className="grid gap-3 sm:grid-cols-2 mb-3"
            aria-label="Selected period account activity totals"
          >
            {totals.map((total) => {
              const direction = feeCenterActivityDirection(total.netChargeCents);
              return (
                <Card key={total.currency} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-[0.12em] text-fg-tertiary">
                        Period total
                      </p>
                      <p className="text-xs text-fg-secondary mt-1">
                        {total.currency} · all account activity
                      </p>
                    </div>
                    <Badge variant={direction === 'Credit' ? 'positive' : 'neutral'}>
                      Net {direction.toLowerCase()}
                    </Badge>
                  </div>
                  <p className="text-xl font-medium tabular-nums mt-3">
                    {formatFeeCenterDelta(total.netChargeCents, total.currency)}
                  </p>
                  <p className="text-xs text-fg-tertiary mt-1">
                    {total.lineCount.toLocaleString('en-US')} ledger lines
                  </p>
                </Card>
              );
            })}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {ordered.map((row) => {
              const direction = feeCenterActivityDirection(row.netChargeCents);
              return (
                <Card
                  key={`${row.periodStart}-${row.currency}-${row.category}-${row.amountType}-${row.amountDescription}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-sm break-words">{row.label}</p>
                      <p className="text-xs text-fg-tertiary mt-1">
                        {formatFeeCenterDate(row.periodStart)} –{' '}
                        {formatFeeCenterDate(row.periodEnd)}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <Badge variant={direction === 'Credit' ? 'positive' : 'neutral'}>
                        {direction}
                      </Badge>
                      <p className="text-sm font-medium tabular-nums mt-2">
                        {formatFeeCenterDelta(row.netChargeCents, row.currency)}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-fg-secondary mt-4">{row.explanation}</p>
                  <p className="text-xs text-fg-secondary mt-3">{feeCenterActivityLinkage(row)}</p>
                  <details className="mt-4 text-xs text-fg-tertiary">
                    <summary className="cursor-pointer hover:text-fg-secondary">
                      Amazon label and ledger counts
                    </summary>
                    <div className="mt-2 space-y-1 pl-3 border-l border-border-subtle">
                      <p>Amount type: {row.amountType}</p>
                      <p>Description: {row.amountDescription}</p>
                      <p>
                        {row.lineCount.toLocaleString('en-US')} ledger lines ·{' '}
                        {row.orderCount.toLocaleString('en-US')} orders ·{' '}
                        {row.skuCount.toLocaleString('en-US')} SKUs
                      </p>
                    </div>
                  </details>
                  <Provenance value={row.provenance} />
                </Card>
              );
            })}
          </div>
        </>
      )}
    </Section>
  );
}

function ActualSection({
  rows,
  fulfillment,
}: {
  rows: FeeCenterActualRow[];
  fulfillment: FeeCenterFulfillmentAuditRow[];
}) {
  const fulfillmentByKey = new Map(
    fulfillment.map((row) => [`${row.sellerSku}-${row.periodStart}-${row.currency}`, row])
  );
  return (
    <Section
      title="Product fee checks"
      description="Product-linked settlement actuals grouped by SKU, month, and currency. Fulfillment estimates and storage evidence remain separate reports below."
    >
      {rows.length === 0 ? (
        <Card>
          <p className="text-sm text-fg-secondary">No settlement fee actuals in this period.</p>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {rows.map((row) => (
            <Card key={`${row.sellerSku}-${row.periodStart}-${row.currency}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-sm break-words">{feeCenterProductTitle(row)}</p>
                  <p className="text-xs text-fg-secondary mt-1">
                    {feeCenterProductIdentifiers(row)}
                  </p>
                  <p className="text-xs text-fg-tertiary mt-1">
                    {formatFeeCenterDate(row.periodStart)} – {formatFeeCenterDate(row.periodEnd)}
                  </p>
                </div>
                <Badge variant="neutral">{row.currency}</Badge>
              </div>
              <dl className="grid grid-cols-2 gap-3 mt-4 text-sm">
                <Metric
                  label="Revenue"
                  value={formatFeeCenterCurrency(row.revenueCents, row.currency)}
                />
                <Metric
                  label="Amazon fees"
                  value={formatFeeCenterCurrency(row.actualFeesCents, row.currency)}
                />
                <Metric label="Fee % of revenue" value={formatFeeCenterPercent(row.feePercent)} />
                <Metric
                  label="Total fees / sold unit"
                  value={formatFeeCenterProductUnitCost(
                    row.feePerUnitSoldCents,
                    row.unitsSold,
                    row.currency
                  )}
                />
              </dl>
              <p className="text-sm text-fg-secondary mt-4">{feeCenterActualSummary(row)}</p>
              {row.breakdown.length > 0 && (
                <div className="mt-5 border-t border-border-subtle pt-4">
                  <p className="text-xs font-medium text-fg-primary">Fee description breakdown</p>
                  <div className="mt-3 space-y-3">
                    {row.breakdown.map((breakdown) => {
                      const direction = feeCenterActivityDirection(breakdown.netChargeCents);
                      return (
                        <div
                          key={`${breakdown.category}-${breakdown.amountType}-${breakdown.amountDescription}`}
                          className="rounded-lg border border-border-subtle bg-surface-elev/40 p-3"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <p className="text-xs font-medium text-fg-primary break-words">
                              {breakdown.label}
                            </p>
                            <Badge variant={direction === 'Credit' ? 'positive' : 'neutral'}>
                              {direction}
                            </Badge>
                          </div>
                          <p className="text-xs text-fg-secondary mt-2">{breakdown.explanation}</p>
                          <p className="text-xs text-fg-tertiary mt-2 tabular-nums">
                            {formatFeeCenterDelta(breakdown.netChargeCents, row.currency)} ·{' '}
                            {breakdown.lineCount.toLocaleString('en-US')} ledger lines ·{' '}
                            {breakdown.orderCount.toLocaleString('en-US')} orders
                          </p>
                          <details className="mt-2 text-[11px] text-fg-tertiary">
                            <summary className="cursor-pointer hover:text-fg-secondary">
                              Amazon label
                            </summary>
                            <p className="mt-1 pl-3 border-l border-border-subtle">
                              {breakdown.amountType} · {breakdown.amountDescription}
                            </p>
                          </details>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="mt-5 border-t border-border-subtle pt-4">
                <p className="text-xs font-medium text-fg-primary">Separate fulfillment evidence</p>
                <p className="text-xs text-fg-secondary mt-1">
                  {feeCenterFulfillmentEvidenceCopy()}
                </p>
                {(() => {
                  const evidence = fulfillmentByKey.get(
                    `${row.sellerSku}-${row.periodStart}-${row.currency}`
                  );
                  if (!evidence) {
                    return (
                      <p className="text-xs text-fg-tertiary mt-2">
                        No matching fulfillment check for this SKU and month.
                      </p>
                    );
                  }
                  return (
                    <p className="text-xs text-fg-secondary mt-2">
                      Charged {formatFeeCenterCurrency(evidence.chargedFeesCents, row.currency)} ·{' '}
                      Expected{' '}
                      {evidence.expectedFeesCents == null
                        ? expectedFeeUnavailableCopy(evidence.expectedReason)
                        : formatFeeCenterCurrency(evidence.expectedFeesCents, row.currency)}{' '}
                      ·{' '}
                      <a
                        href="#fulfillment-audit-heading"
                        className="text-accent-fg hover:underline"
                      >
                        see the full fulfillment audit
                      </a>
                    </p>
                  );
                })()}
              </div>
              <Provenance value={row.provenance} />
            </Card>
          ))}
        </div>
      )}
    </Section>
  );
}

function FulfillmentSection({ rows }: { rows: FeeCenterFulfillmentAuditRow[] }) {
  const ordered = rankFulfillmentByDelta(rows);
  return (
    <Section
      title="Fulfillment audit"
      description="Charged fulfillment fees compared with the fresh same-price estimate from the inventory snapshot."
    >
      <p className="text-xs text-fg-secondary border border-border-subtle bg-surface rounded-xl px-4 py-3 mb-3">
        Use this as an investigation queue. Remeasure the fee in Seller Central manually before
        changing a listing or price.
      </p>
      {ordered.length === 0 ? (
        <Card>
          <p className="text-sm text-fg-secondary">No fulfillment-fee actuals in this period.</p>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {ordered.map((row) => (
            <Card key={`${row.sellerSku}-${row.periodStart}-${row.currency}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-sm break-words">{row.sellerSku}</p>
                  <p className="text-xs text-fg-tertiary mt-1">
                    {formatFeeCenterDate(row.periodStart)} – {formatFeeCenterDate(row.periodEnd)}
                  </p>
                </div>
                <Badge variant="neutral">{row.units.toLocaleString('en-US')} units</Badge>
              </div>
              <dl className="grid grid-cols-2 gap-3 mt-4 text-sm">
                <Metric
                  label="Charged"
                  value={formatFeeCenterCurrency(row.chargedFeesCents, row.currency)}
                />
                <Metric
                  label="Expected"
                  value={
                    row.expectedFeesCents == null
                      ? expectedFeeUnavailableCopy(row.expectedReason)
                      : formatFeeCenterCurrency(row.expectedFeesCents, row.currency)
                  }
                />
                <Metric
                  label="Charged / unit"
                  value={
                    row.chargedPerUnitCents == null
                      ? '—'
                      : formatFeeCenterCurrency(row.chargedPerUnitCents, row.currency)
                  }
                />
                <Metric
                  label="Dollar delta"
                  value={formatFeeCenterDelta(row.deltaCents, row.currency)}
                />
              </dl>
              <Provenance value={row.provenance} />
            </Card>
          ))}
        </div>
      )}
    </Section>
  );
}

function StorageSection({ rows }: { rows: FeeCenterStorageRow[] }) {
  return (
    <Section
      title="Storage & velocity"
      description="Monthly storage cost alongside sold units and calendar-day velocity. Storage dollars remain separate from settlement actuals."
    >
      {rows.length === 0 ? (
        <Card>
          <p className="text-sm text-fg-secondary">No storage or sales rows in this period.</p>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {rows.map((row) => (
            <Card key={`${row.sellerSku}-${row.periodStart}`}>
              <p className="font-medium text-sm break-words">{row.sellerSku}</p>
              <p className="text-xs text-fg-tertiary mt-1">
                {formatFeeCenterDate(row.periodStart)} – {formatFeeCenterDate(row.periodEnd)}
              </p>
              <dl className="grid grid-cols-2 gap-3 mt-4 text-sm">
                <Metric
                  label="Storage cost"
                  value={
                    row.estimatedStorageFeeCents == null
                      ? unavailable('storage coverage incomplete')
                      : formatFeeCenterCurrency(row.estimatedStorageFeeCents)
                  }
                />
                <Metric
                  label="Units sold"
                  value={
                    row.unitsSold == null
                      ? formatFeeCenterVelocityUnavailable()
                      : row.unitsSold.toLocaleString('en-US')
                  }
                />
                <Metric
                  label="Velocity"
                  value={
                    row.unitsPerDay == null
                      ? formatFeeCenterVelocityUnavailable()
                      : formatFeeCenterUnitsPerDay(row.unitsPerDay)
                  }
                />
                <Metric
                  label="Storage / revenue"
                  value={formatFeeCenterPercent(row.storagePercentOfRevenue)}
                />
              </dl>
              <Provenance value={row.provenance} />
            </Card>
          ))}
        </div>
      )}
    </Section>
  );
}

function AgedSection({ rows }: { rows: FeeCenterAgedRow[] }) {
  return (
    <Section
      title="Aged inventory"
      description="Report-derived age buckets and units. Amazon does not supply surcharge dates in this report, so countdowns are not inferred."
    >
      {rows.length === 0 ? (
        <Card>
          <p className="text-sm text-fg-secondary">No aged-inventory facts in this period.</p>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {rows.map((row) => (
            <Card key={`${row.sellerSku}-${row.snapshotDate}-${row.ageBucket}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-sm break-words">{row.sellerSku}</p>
                  <p className="text-xs text-fg-tertiary mt-1">
                    Snapshot {formatFeeCenterDate(row.snapshotDate)}
                  </p>
                </div>
                <Badge variant="neutral">{row.ageBucket}</Badge>
              </div>
              <dl className="grid grid-cols-2 gap-3 mt-4 text-sm">
                <Metric label="Units" value={row.units.toLocaleString('en-US')} />
                <Metric label="Surcharge" value={agingDateCopy(row.surchargeDate)} />
                <Metric
                  label="Countdown"
                  value={formatFeeCenterSurchargeCountdown(row.daysUntilSurcharge)}
                />
              </dl>
              <Provenance value={row.provenance} />
            </Card>
          ))}
        </div>
      )}
    </Section>
  );
}

function FeeChangesSection({ rows }: { rows: FeeCenterFeeChangeRow[] }) {
  return (
    <Section
      title="Fee changes"
      description="Append-only alerts where the fee changed while the checked price stayed unchanged."
    >
      {rows.length === 0 ? (
        <Card>
          <p className="text-sm text-fg-secondary">
            No material fee changes observed in this period.
          </p>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {rows.map((row) => (
            <Card key={`${row.sellerSku}-${row.observedAt}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-sm break-words">{row.sellerSku}</p>
                  <p className="text-xs text-fg-tertiary mt-1">
                    Observed {formatFeeCenterDate(row.observedAt)}
                  </p>
                </div>
                <Badge variant={row.material ? 'warning' : 'neutral'}>
                  {row.material ? 'Material' : 'Review'}
                </Badge>
              </div>
              <dl className="grid grid-cols-2 gap-3 mt-4 text-sm">
                <Metric
                  label="Previous fee"
                  value={formatFeeCenterCurrency(row.previousFeeCents)}
                />
                <Metric label="New fee" value={formatFeeCenterCurrency(row.newFeeCents)} />
                <Metric label="Change" value={formatFeeCenterDelta(row.deltaCents)} />
              </dl>
              <p className="text-xs text-fg-secondary mt-4">
                Checked price unchanged: {formatFeeCenterCurrency(row.previousPriceCents)} →{' '}
                {formatFeeCenterCurrency(row.newPriceCents)}.
              </p>
              <Provenance value={row.provenance} />
            </Card>
          ))}
        </div>
      )}
    </Section>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-[0.12em] text-fg-tertiary">{label}</dt>
      <dd className="mt-1 tabular-nums text-fg-primary break-words">{value}</dd>
    </div>
  );
}
